import {
  useMemo,
  useRef,
  useEffect,
  RefObject,
  ElementType,
  ComponentPropsWithoutRef,
  ReactNode,
} from 'react';
import classnames from 'classnames';
import useStyles from './Button.styles';

export const PRIMARY = 'primary';
export const SECONDARY = 'secondary';
export const TERTIARY = 'tertiary';
export const BLOCK = 'block';
export const NORMAL = 'normal';
export const SMALL = 'small';
export const EXTRA_SMALL = 'extraSmall';

export interface IButtonProps<T extends ElementType> {
  /** The tag or component to render the button styling to  */
  component?: T | 'button';
  /** The type of glu button you want */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'block';
  /** The size of the glu button you want */
  size?: 'normal' | 'small' | 'extraSmall';
  /** Automatically set focus on button. */
  autoFocus?: boolean;
  /** The text of the button for simple buttons, used in preference to children */
  text?: string;
  /** Data-qa attribute for automated QA testing */
  dataQa?: string;
  /** a ref to be used on the component */
  innerRef?: RefObject<HTMLButtonElement>;
  /** Icon displayed to the left of children */
  leftIcon?: ReactNode;
  /** Icon displayed to the right of children */
  rightIcon?: ReactNode;
  /** Used to display an alternative prominent color for the button */
  prominent?: boolean;
}

export type TButtonProps<T extends ElementType = 'button'> = IButtonProps<T> &
  ComponentPropsWithoutRef<T>;

let uniqueId = 0;

/**
 * Basic Glu button supporting the primary, secondary, and tertiary types.
 */
export const Button = <T extends ElementType = 'button'>({
  'aria-busy': ariaBusy,
  autoFocus = false,
  children,
  className,
  component: Component = 'button',
  dataQa,
  innerRef,
  leftIcon,
  onClick,
  prominent,
  rightIcon,
  size = NORMAL,
  text,
  type = 'button',
  variant = PRIMARY,
  ...rest
}: TButtonProps<T>) => {
  const classes = useStyles();
  const buttonRef = useRef(null);
  const ref = innerRef || buttonRef;
  const dataQAFallback = useMemo(() => `button-${uniqueId++}`, []); // eslint-disable-line no-plusplus

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
  }, [autoFocus, ref]);

  return (
    <Component
      ref={ref}
      aria-busy={ariaBusy}
      className={classnames(
        classes.root,
        classes[variant],
        classes[size],
        { [classes.prominent]: prominent },
        className
      )}
      data-qa={dataQa || dataQAFallback}
      onClick={ariaBusy ? undefined : onClick}
      type={type}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {leftIcon || rightIcon ? (
        <span className={classnames(classes.iconContainer)}>
          {leftIcon && (
            <span className={classnames(classes.icon, classes.leftIcon)}>{leftIcon}</span>
          )}
          {text || children}
          {rightIcon && (
            <span className={classnames(classes.icon, classes.rightIcon)}>{rightIcon}</span>
          )}
        </span>
      ) : (
        text || children
      )}
    </Component>
  );
};

export default Button;
